import styled, { css } from 'styled-components';
import { InfoContainer, Radio, RadioLabel, Text } from '@lmn/design-system';
import React, { useEffect } from 'react';
import { useSearchParams } from 'next/navigation';
import { IconCircleInfoSolid } from '@/app/common/icons/libraryIcons';
import { URLParameters } from '@/app/common/urlParameters';
import type { BrandedFare } from '@/app/api/hotel-detail/branded-fares/route';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider';
import { useSelectedMealPlan } from '@/app/components/hotel-detail/SelectedMealPlanProvider';
import { useIsLgOrGreater } from '@/app/components/hotel-detail/hooks/useMediaSSR';
import { v4 as uuidv4 } from 'uuid';
import { createBrandedFaresAvailablePayload } from '@/app/common/starTrack/events/brandedFaresAvailable.ts';
import { createBrandedFaresShownPayload } from '@/app/common/starTrack/events/brandedFaresShown.ts';
import { createBrandedFaresClickedPayload } from '@/app/common/starTrack/events/brandedFaresClicked.ts';
import usePriceDisplay from '@/app/components/hotel-detail/hooks/usePriceDisplay.ts';
import { getPriceIncrementLabel } from '@/app/components/hotel-detail/changeTransport/priceIncrement.ts';
import { createPriceByDisplayType } from '@/app/components/search/utils/formatting.ts';
import { starfishCssUrl } from '@/app/common/icons/starfish';
import { useBrandedFares } from '../hooks/useBrandedFares';
import { useTranslation } from '../../translations/Translation';
import { useInitialParams } from '../InitialParamsProvider';
import { IconCheck } from '../common/icons';
import { DefaultBrandedFare, FareCardSkeleton } from './DefaultBrandedFare';
import { StyledIconCoins } from './BrandedFaresCommon';

type BrandedFaresContext = 'flight-card' | 'evo-flight-section';

const StyledRadio = styled(Radio)`
  padding: 0;
  flex-grow: 1;
  ${RadioLabel} {
    font-size: ${(p) => p.theme.commons.font.sizes.base};
    line-height: ${(p) => p.theme.commons.font.heights.base};
    padding: 0;
    width: 100%;
  }
`;

export const BrandedFaresContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const BrandedFareCard = styled.div<{ $selected: boolean; $evo?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  border-radius: 8px;
  padding: 16px 16px 12px;
  background: ${({ theme }) => theme.commons.colors.gray0};
  box-sizing: border-box;
  cursor: pointer;
  box-shadow: ${({ theme }) => theme.kallisto.shadow.mediumDownShadow};

  .branded-fare-radio {
    cursor: pointer;
  }

  ${({ $evo, theme }) =>
    $evo &&
    css`
      background-color: ${theme.kallisto.color.background.static.backgroundLayer02};
      border: 1px solid ${theme.kallisto.color.border.static.borderNeutral};
      border-radius: 16px;
      padding: 16px;
      box-shadow: none;
      outline: none;

      /* Pad the 18px radio circle to the 20px service-icon slot so the fare
         title aligns with the service-row text below it. */
      [data-testid='radio-indicator'] {
        margin-right: 2px;
      }

      /* Cards share a row height via the grid (align-items: stretch). Stop the radio
         from absorbing that extra height (which dropped the title towards the middle):
         keep content top-aligned and let the surplus fall as whitespace at the bottom. */
      ${StyledRadio} {
        flex-grow: 0;
      }

      &:has(.branded-fare-radio:checked) {
        border-color: ${theme.kallisto.color.border.interactive.borderPrimaryDefault};
        outline: none;
      }
    `}

  ${({ $selected, $evo }) =>
    $selected &&
    !$evo &&
    css`
      outline: 2px solid ${({ theme }) => theme.kallisto.color.border.interactive.borderPrimaryDefault};
    `}

  /* Keyboard focus ring for the Tab-navigable card (defined last so it wins over the
     $evo/$selected outline rules). */
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.kallisto.color.border.interactive.borderPrimaryDefault};
    outline-offset: 2px;
  }
`;

const BrandedFareLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const BrandedFarePriceContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 2px;
`;

const BrandedFarePrice = styled(Text).attrs({ variant: 'Title4' as const })`
  white-space: nowrap;
  text-align: right;
`;

const ServiceContainer = styled.div`
  display: flex;
  flex-direction: column;

  span {
    padding: 4px 0;
  }
`;

const Service = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

// MPH-479: the service icon must sit on the first text line (Figma), not centered
// vertically across a multi-line row, so the Evo row overrides align-items and the
// coins icon gets the same 6px top offset as the check/cross images.
const EvoService = styled(Service)<{ $strikethrough?: boolean }>`
  align-items: flex-start;

  svg,
  [data-library-icon] {
    margin-top: 6px;
    flex-shrink: 0;
  }

  ${({ $strikethrough }) =>
    $strikethrough &&
    css`
      span {
        text-decoration: line-through;
        color: ${({ theme }) => theme.kallisto.color.text.static.textSecondary};
      }
    `}
`;

const EvoDivider = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.kallisto.color.border.static.borderNeutral};
  margin: 4px 0;
`;

const EvoSelectedLabel = styled(Text).attrs({ variant: 'Title4' as const })`
  color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
  text-align: right;
`;

// MPH-479: render the Figma glyphs through a CSS mask so the icon colour follows the brand
// token (iconSuccess/iconSecondary) instead of the SVG's fixed fill — the raw SVGs hard-code
// #427B09/#726E73, which is only correct on pinkPanther. The 6px top offset (4px list-item +
// 2px icon container per Figma) keeps the 20px glyph centered on the first 24px text line.
const EvoServiceIcon = styled.span`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  margin-top: 6px;
  mask-repeat: no-repeat;
  mask-position: center;
  mask-size: 20px 20px;
  -webkit-mask-repeat: no-repeat;
  -webkit-mask-position: center;
  -webkit-mask-size: 20px 20px;
`;

const EvoIconCheck = styled(EvoServiceIcon)`
  background-color: ${({ theme }) => theme.kallisto.color.icon.static.iconSuccess};
  mask-image: url('${starfishCssUrl('Semantics', 'icon-check')}');
  -webkit-mask-image: url('${starfishCssUrl('Semantics', 'icon-check')}');
`;

const EvoIconCross = styled(EvoServiceIcon)`
  background-color: ${({ theme }) => theme.kallisto.color.icon.static.iconSecondary};
  mask-image: url('${starfishCssUrl('Semantics', 'icon-cross')}');
  -webkit-mask-image: url('${starfishCssUrl('Semantics', 'icon-cross')}');
`;

const EvoBannerIcon = () => <IconCircleInfoSolid size={20} />;

const EvoMultiFareBannerWrapper = styled.div`
  padding-bottom: 16px;
`;

type BrandedFaresProps = {
  context: BrandedFaresContext;
  inbound: TransportBoundType;
  outbound: TransportBoundType;
  transportId: string;
  priceIncrement: number;
  eligibleBrandedFares: boolean;
  disableBrandedFareSelection?: boolean;
  numberOfBaggagePiecesIncluded: number | null;
  hideTitle?: boolean;
  onLoadFare: () => void;
  onSelectBrandedFare?: (brandedFare: BrandedFare | undefined) => void;
} & React.HTMLAttributes<HTMLDivElement>;

export function BrandedFares(props: BrandedFaresProps) {
  const {
    context,
    inbound,
    outbound,
    transportId,
    disableBrandedFareSelection = false,
    priceIncrement,
    eligibleBrandedFares,
    numberOfBaggagePiecesIncluded,
    hideTitle,
    onLoadFare,
    onSelectBrandedFare,
    ...rest
  } = props;

  const { starTrack } = useCommonClient();
  const initialParams = useInitialParams();

  useEffect(() => {
    if (eligibleBrandedFares) {
      const flightClickId = initialParams.flightClickId || uuidv4().slice(0, 32);
      starTrack.brandedFaresAvailable(
        createBrandedFaresAvailablePayload({
          initialParams,
          flightClickId,
        })
      );
    }
  }, []);

  return !eligibleBrandedFares ? (
    <DefaultBrandedFare
      inbound={inbound}
      outbound={outbound}
      numberOfBaggagePiecesIncluded={numberOfBaggagePiecesIncluded}
      onLoadFare={onLoadFare}
      hideTitle={hideTitle}
      {...rest}
    />
  ) : (
    <CustomBrandedFares
      context={context}
      transportId={transportId}
      priceIncrement={priceIncrement}
      disableBrandedFareSelection={disableBrandedFareSelection}
      inbound={inbound}
      outbound={outbound}
      onLoadFare={onLoadFare}
      onSelectBrandedFare={onSelectBrandedFare}
      numberOfBaggagePiecesIncluded={numberOfBaggagePiecesIncluded}
      hideTitle={hideTitle}
      {...rest}
    />
  );
}

export function CustomBrandedFares(
  props: {
    context: BrandedFaresContext;
    transportId: string;
    priceIncrement: number;
    disableBrandedFareSelection?: boolean;
    numberOfBaggagePiecesIncluded: number | null;
    inbound: TransportBoundType;
    outbound: TransportBoundType;
    hideTitle?: boolean;
    onLoadFare: () => void;
    onSelectBrandedFare?: (brandedFare: BrandedFare | undefined) => void;
  } & React.HTMLAttributes<HTMLDivElement>
) {
  const {
    context,
    transportId,
    priceIncrement,
    disableBrandedFareSelection = false,
    inbound,
    outbound,
    numberOfBaggagePiecesIncluded,
    hideTitle,
    onLoadFare,
    onSelectBrandedFare,
    ...rest
  } = props;
  const { starTrack } = useCommonClient();
  const { t } = useTranslation();
  const initialParams = useInitialParams();
  const { localization, searchInfoData } = usePageWrapperData();
  const { data: brandedFaresData, isLoading } = useBrandedFares(transportId, priceIncrement);
  const { selectedMealPlan } = useSelectedMealPlan();
  const { showTotalPrice, toDisplayPrice } = usePriceDisplay();
  const priceUnitLabel = t(
    showTotalPrice ? 'vacation.oss.cards.total' : 'vacation.select.room.price.dp.perperson.short'
  );
  const [selectedBrandedFareForCurrentTransport, setSelectedBrandedFareForCurrentTransport] = React.useState<
    BrandedFare | undefined
  >(undefined);
  const selectedBrandedFare = selectedBrandedFareForCurrentTransport;

  const searchParams = useSearchParams();

  const brandedFareId = searchParams.get(URLParameters.BRANDED_FARE_ID) || initialParams.selectedBrandedFareId;
  const isBrandedFareDataError =
    !isLoading && (!Array.isArray(brandedFaresData?.fares) || !brandedFaresData.fares.length);

  const onChangeBrandedFare = (brandedFare: BrandedFare) => {
    onSelectBrandedFare?.(brandedFare);
    setSelectedBrandedFareForCurrentTransport(brandedFare);
  };

  useEffect(() => {
    if (isLoading || !brandedFaresData) {
      onSelectBrandedFare?.(undefined);
    }
  }, [isLoading, brandedFaresData]);

  const onBrandedFareClicked = (brandedFare: BrandedFare) => {
    if (!disableBrandedFareSelection && selectedBrandedFare?.fareId !== brandedFare.fareId) {
      starTrack.brandedFaresClicked(
        createBrandedFaresClickedPayload({
          initialParams,
          brandedFare,
          selectedMealPlan,
          currency: localization.currency,
        })
      );
      onChangeBrandedFare(brandedFare);
    }
  };

  useEffect(() => {
    if (!isBrandedFareDataError) {
      // Prioritize URL parameter (brandedFareId) over context state (selectedBrandedFare)
      // to ensure selection persists when navigating back from checkout
      const targetFareId = brandedFareId || selectedBrandedFare?.fareId;
      const preSelectedBrandedFare =
        brandedFaresData?.fares.find((brandedFare) => brandedFare.fareId === targetFareId) ||
        brandedFaresData?.fares[0];

      if (preSelectedBrandedFare) {
        onChangeBrandedFare(preSelectedBrandedFare);
      }
    }
  }, [brandedFaresData]);

  useEffect(() => {
    if (!isBrandedFareDataError && brandedFaresData && selectedMealPlan) {
      // Prioritize URL parameter to maintain selection consistency
      const targetFareId = brandedFareId || selectedBrandedFare?.fareId;
      starTrack.brandedFaresShown(
        createBrandedFaresShownPayload({
          initialParams,
          brandedFaresData,
          selectedMealPlan,
          currency: localization.currency,
          searchTraceParent: searchInfoData?.searchTraceParent,
          targetFareId,
        })
      );
    }
    // initialParams is read fresh from the closure but intentionally NOT a dependency: rotating
    // the flightClickId while these fares are mounted (confirming an Evo flight change,
    // MPH-646) must not re-emit the old fares under the new session id — the event re-fires
    // with the new id when the new fares data lands.
  }, [searchInfoData?.searchTraceParent, brandedFaresData, selectedMealPlan]);

  if (!isLoading && isBrandedFareDataError) {
    return (
      <DefaultBrandedFare
        inbound={inbound}
        outbound={outbound}
        numberOfBaggagePiecesIncluded={numberOfBaggagePiecesIncluded}
        onLoadFare={onLoadFare}
        hideTitle={hideTitle}
        {...rest}
      />
    );
  }

  const faresToShow =
    disableBrandedFareSelection && selectedBrandedFare
      ? ([selectedBrandedFare] as BrandedFare[])
      : brandedFaresData?.fares;

  const showEvoMultiFareBanner =
    context === 'evo-flight-section' && !isLoading && !!faresToShow && faresToShow.length > 1;

  return (
    <>
      {hideTitle ? null : (
        <Text
          variant="Title4"
          css={`
            padding-bottom: 12px;
          `}
        >
          {disableBrandedFareSelection || faresToShow?.length === 1
            ? t('vacation.select.flight_detail_modal.included_fare')
            : t('vacation.select.flight_detail_modal.branded_fare.title')}
        </Text>
      )}
      {showEvoMultiFareBanner && (
        <EvoMultiFareBannerWrapper>
          <InfoContainer
            icon={EvoBannerIcon}
            message={t('vacation.oss.evo_flights.bundled_fares.banner')}
            type="specific"
            hasBorder={false}
          />
        </EvoMultiFareBannerWrapper>
      )}
      <BrandedFaresContainer
        {...rest}
        {...(disableBrandedFareSelection
          ? {}
          : {
              role: 'radiogroup',
              'aria-label': t('vacation.select.flight_detail_modal.branded_fare.title'),
            })}
      >
        {isLoading ? (
          <>
            <FareCardSkeleton />
            {/* <FareCardSkeleton /> */}
          </>
        ) : (
          faresToShow?.map((brandedFare) => {
            const isEvo = context === 'evo-flight-section';
            const isSelected = selectedBrandedFare?.fareId === brandedFare.fareId;
            // In EVO, non-selected cards show the price difference relative to the currently
            // selected fare, so the previously-selected (cheaper) card shows e.g. "- £124"
            // instead of nothing. Non-EVO keeps the absolute difference from the base fare.
            const displayedPriceDifference = toDisplayPrice(
              isEvo
                ? brandedFare.priceDifference - (selectedBrandedFare?.priceDifference ?? 0)
                : brandedFare.priceDifference
            );
            const zeroPriceDiff =
              !displayedPriceDifference ||
              createPriceByDisplayType(
                Math.abs(displayedPriceDifference),
                localization.configValuesApp.priceIncrementDisplayType
              ) === 0;
            const priceLabel = getPriceIncrementLabel({ priceIncrement: displayedPriceDifference, localization });
            return (
              <BrandedFareCard
                key={`${context}-${brandedFare.fareId}`}
                $selected={!disableBrandedFareSelection && isSelected}
                $evo={isEvo}
                {...(disableBrandedFareSelection
                  ? {}
                  : {
                      role: 'radio',
                      'aria-checked': isSelected,
                      'aria-label': brandedFare.fareLabel,
                      tabIndex: 0,
                      onKeyDown: (event: React.KeyboardEvent) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onBrandedFareClicked(brandedFare);
                        }
                      },
                    })}
                onClick={(event) => {
                  event.stopPropagation();
                  onBrandedFareClicked(brandedFare);
                }}
              >
                {disableBrandedFareSelection ? (
                  <BrandedFareLabel>
                    <Text variant="Title5">{brandedFare.fareLabel}</Text>
                  </BrandedFareLabel>
                ) : (
                  <StyledRadio
                    label={
                      <BrandedFareLabel>
                        <Text variant="Title5">{brandedFare.fareLabel}</Text>
                        {isEvo && isSelected ? (
                          <EvoSelectedLabel>{t('vacation.oss.evo_flights.bundled_fares.selected')}</EvoSelectedLabel>
                        ) : !zeroPriceDiff && priceLabel ? (
                          <BrandedFarePriceContainer>
                            <BrandedFarePrice>{priceLabel}</BrandedFarePrice>
                            <Text variant="BodySmall" weight="bold">
                              {priceUnitLabel}
                            </Text>
                          </BrandedFarePriceContainer>
                        ) : undefined}
                      </BrandedFareLabel>
                    }
                    isOnHover={true}
                    name={context}
                    value={brandedFare.fareId}
                    data-testid={`branded-fare-${brandedFare.fareId}`}
                    hoverBackground={false}
                    checked={selectedBrandedFare?.fareId === brandedFare.fareId}
                    readOnly={false}
                    inputClassName="branded-fare-radio"
                    /* The card is the keyboard target (role=radio, Tab-focusable); keep this
                       native radio for visuals/mouse only, out of the tab order and SR tree. */
                    tabIndex={-1}
                    aria-hidden={true}
                    onChange={(event) => {
                      event.stopPropagation();
                      onBrandedFareClicked(brandedFare);
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                    }}
                  />
                )}

                {isEvo ? (
                  <BrandedFareServicesEvo
                    brandedFare={brandedFare}
                    allFares={faresToShow}
                    css={css`
                      border-top: 1px solid ${(p) => p.theme.commons.colors.gray100};
                      padding-top: 12px;
                    `}
                  />
                ) : (
                  <BrandedFareServices
                    brandedFare={brandedFare}
                    css={css`
                      border-top: 1px solid ${(p) => p.theme.commons.colors.gray100};
                      padding-top: 12px;
                    `}
                  />
                )}
              </BrandedFareCard>
            );
          })
        )}
      </BrandedFaresContainer>
    </>
  );
}

export function BrandedFareServices(props: { brandedFare: BrandedFare } & React.HTMLAttributes<HTMLDivElement>) {
  const { brandedFare, ...rest } = props;
  const { t } = useTranslation();
  return (
    <ServiceContainer {...rest}>
      {brandedFare.smallBaggage.included ? (
        <Service>
          <IconCheck />
          <Text variant="BodySmall" forceTag="span">
            {t('vacation.select.brandedfares.services.smallBaggage.included')}
          </Text>
        </Service>
      ) : undefined}
      {brandedFare.cabinBaggage ? (
        <Service>
          <IconCheck />
          <Text variant="BodySmall" forceTag="span">
            {brandedFare.cabinBaggage.weight
              ? !brandedFare.cabinBaggage.numberOfPieces || brandedFare.cabinBaggage.numberOfPieces === '1'
                ? t('vacation.select.brandedfares.services.cabinBaggage.included.singular', {
                    num: brandedFare.cabinBaggage.numberOfPieces,
                    weight: brandedFare.cabinBaggage.weight,
                    weightUnit: brandedFare.cabinBaggage.weightUnitOfMeasure,
                  })
                : t('vacation.select.brandedfares.services.cabinBaggage.included.plural', {
                    num: brandedFare.cabinBaggage.numberOfPieces,
                    weight: brandedFare.cabinBaggage.weight,
                    weightUnit: brandedFare.cabinBaggage.weightUnitOfMeasure,
                  })
              : t('vacation.select.brandedfares.services.cabinBaggage.included', {
                  weight: brandedFare.cabinBaggage.weight,
                  weightUnit: brandedFare.cabinBaggage.weightUnitOfMeasure,
                })}
          </Text>
        </Service>
      ) : undefined}
      {brandedFare.holdBaggage ? (
        <Service>
          <IconCheck />
          <Text variant="BodySmall" forceTag="span">
            {brandedFare.holdBaggage.weight
              ? !brandedFare.holdBaggage.numberOfPieces || brandedFare.holdBaggage.numberOfPieces === '1'
                ? t('vacation.select.brandedfares.services.holdBaggage.included.singular', {
                    num: brandedFare.holdBaggage.numberOfPieces,
                    weight: brandedFare.holdBaggage.weight,
                    weightUnit: brandedFare.holdBaggage.weightUnitOfMeasure,
                  })
                : t('vacation.select.brandedfares.services.holdBaggage.included.plural', {
                    num: brandedFare.holdBaggage.numberOfPieces,
                    weight: brandedFare.holdBaggage.weight,
                    weightUnit: brandedFare.holdBaggage.weightUnitOfMeasure,
                  })
              : t('vacation.select.brandedfares.services.holdBaggage.included', {
                  weight: brandedFare.holdBaggage.weight,
                  weightUnit: brandedFare.holdBaggage.weightUnitOfMeasure,
                })}
          </Text>
        </Service>
      ) : undefined}
      {brandedFare.seatSelection.included || brandedFare.seatSelection.charge ? (
        <Service>
          {brandedFare.seatSelection.charge ? <StyledIconCoins /> : <IconCheck />}
          <Text variant="BodySmall" forceTag="span">
            {brandedFare.seatSelection.included
              ? t('vacation.select.brandedfares.services.seatSelection.included')
              : t('vacation.select.brandedfares.services.seatSelection.charged')}
          </Text>
        </Service>
      ) : undefined}
      {brandedFare.priorityBoarding.included ? (
        <Service>
          <IconCheck />
          <Text variant="BodySmall" forceTag="span">
            {t('vacation.select.brandedfares.services.priorityBoarding.included')}
          </Text>
        </Service>
      ) : undefined}
      {brandedFare.flightChange.included || brandedFare.flightChange.charge ? (
        <Service>
          {brandedFare.flightChange.charge ? <StyledIconCoins /> : <IconCheck />}
          <Text variant="BodySmall" forceTag="span">
            {brandedFare.flightChange.included
              ? t('vacation.select.brandedfares.services.flightChange.included')
              : t('vacation.select.brandedfares.services.flightChange.charged')}
          </Text>
        </Service>
      ) : undefined}
      {brandedFare.cancellation.included || brandedFare.cancellation.charge ? (
        <Service>
          {brandedFare.cancellation.charge ? <StyledIconCoins /> : <IconCheck />}
          <Text variant="BodySmall" forceTag="span">
            {brandedFare.cancellation.charge
              ? t('vacation.select.brandedfares.services.cancellation.charged')
              : brandedFare.cancellation.details === 'original-payment-method'
                ? t('vacation.select.brandedfares.services.cancellation.original_payment_method')
                : brandedFare.cancellation.details === 'virtual-credit'
                  ? t('vacation.select.brandedfares.services.cancellation.virtual_credit')
                  : t('vacation.select.brandedfares.services.cancellation.included')}
          </Text>
        </Service>
      ) : undefined}
      {brandedFare.loungeAccess.included || brandedFare.loungeAccess.charge ? (
        <Service>
          {brandedFare.loungeAccess.charge ? <StyledIconCoins /> : <IconCheck />}
          <Text variant="BodySmall" forceTag="span">
            {brandedFare.loungeAccess.included
              ? t('vacation.select.brandedfares.services.loungeAccess.included')
              : t('vacation.select.brandedfares.services.loungeAccess.charged')}
          </Text>
        </Service>
      ) : undefined}
      {brandedFare.comfort.included || brandedFare.comfort.charge ? (
        <Service>
          {brandedFare.comfort.charge ? <StyledIconCoins /> : <IconCheck />}
          <Text variant="BodySmall" forceTag="span">
            {brandedFare.comfort.included
              ? t('vacation.select.brandedfares.services.comfort.included')
              : t('vacation.select.brandedfares.services.comfort.charged')}
          </Text>
        </Service>
      ) : undefined}
      {brandedFare.sustainability?.included ? (
        <Service>
          <IconCheck />
          <Text variant="BodySmall" forceTag="span">
            {t('vacation.select.brandedfares.services.sustainability.included', {
              percent1: brandedFare.sustainability.fuel,
              percent2: brandedFare.sustainability.climateProjects,
            })}
          </Text>
        </Service>
      ) : undefined}
    </ServiceContainer>
  );
}

export function BrandedFareServicesEvo(
  props: { brandedFare: BrandedFare; allFares: BrandedFare[] } & React.HTMLAttributes<HTMLDivElement>
) {
  const { brandedFare, allFares, ...rest } = props;
  const { t } = useTranslation();
  const { commonData } = usePageWrapperData();
  // MPH-398: mobile lists only included services (✓); desktop keeps excluded ones struck through (✗)
  const isDesktop = useIsLgOrGreater(commonData.isDesktopDevice);

  const fareWithCabinBag = allFares.find((f) => !!f.cabinBaggage);
  const fareWithHoldBag = allFares.find((f) => !!f.holdBaggage);
  const fareWithCancellation = allFares.find((f) => f.cancellation.included || f.cancellation.charge);

  type ServiceItem = { key: string; hasIt: boolean; icon: 'check' | 'coins'; label: string };

  // Group 1: Baggage
  const baggageItems: ServiceItem[] = [];

  if (allFares.some((f) => f.smallBaggage.included)) {
    baggageItems.push({
      key: 'smallBaggage',
      hasIt: brandedFare.smallBaggage.included,
      icon: 'check',
      label: t('vacation.select.brandedfares.services.smallBaggage.included'),
    });
  }

  if (fareWithCabinBag) {
    const ref = (brandedFare.cabinBaggage ? brandedFare : fareWithCabinBag).cabinBaggage!;
    const label = ref.weight
      ? !ref.numberOfPieces || ref.numberOfPieces === '1'
        ? t('vacation.select.brandedfares.services.cabinBaggage.included.singular', {
            num: ref.numberOfPieces,
            weight: ref.weight,
            weightUnit: ref.weightUnitOfMeasure,
          })
        : t('vacation.select.brandedfares.services.cabinBaggage.included.plural', {
            num: ref.numberOfPieces,
            weight: ref.weight,
            weightUnit: ref.weightUnitOfMeasure,
          })
      : t('vacation.select.brandedfares.services.cabinBaggage.included', {
          weight: ref.weight,
          weightUnit: ref.weightUnitOfMeasure,
        });
    baggageItems.push({ key: 'cabinBaggage', hasIt: !!brandedFare.cabinBaggage, icon: 'check', label });
  }

  if (fareWithHoldBag) {
    const ref = (brandedFare.holdBaggage ? brandedFare : fareWithHoldBag).holdBaggage!;
    const label = ref.weight
      ? !ref.numberOfPieces || ref.numberOfPieces === '1'
        ? t('vacation.select.brandedfares.services.holdBaggage.included.singular', {
            num: ref.numberOfPieces,
            weight: ref.weight,
            weightUnit: ref.weightUnitOfMeasure,
          })
        : t('vacation.select.brandedfares.services.holdBaggage.included.plural', {
            num: ref.numberOfPieces,
            weight: ref.weight,
            weightUnit: ref.weightUnitOfMeasure,
          })
      : t('vacation.select.brandedfares.services.holdBaggage.included', {
          weight: ref.weight,
          weightUnit: ref.weightUnitOfMeasure,
        });
    baggageItems.push({ key: 'holdBaggage', hasIt: !!brandedFare.holdBaggage, icon: 'check', label });
  }

  // Group 2: Seat selection + priority boarding
  const seatItems: ServiceItem[] = [];

  if (allFares.some((f) => f.seatSelection.included || f.seatSelection.charge)) {
    const hasIt = brandedFare.seatSelection.included || brandedFare.seatSelection.charge;
    const refFare = hasIt ? brandedFare : allFares.find((f) => f.seatSelection.included || f.seatSelection.charge)!;
    const label = refFare.seatSelection.included
      ? t('vacation.select.brandedfares.services.seatSelection.included')
      : t('vacation.select.brandedfares.services.seatSelection.charged');
    seatItems.push({ key: 'seatSelection', hasIt, icon: brandedFare.seatSelection.charge ? 'coins' : 'check', label });
  }

  if (allFares.some((f) => f.priorityBoarding.included)) {
    seatItems.push({
      key: 'priorityBoarding',
      hasIt: brandedFare.priorityBoarding.included,
      icon: 'check',
      label: t('vacation.select.brandedfares.services.priorityBoarding.included'),
    });
  }

  // Group 3: Cancellation policy
  const cancellationItems: ServiceItem[] = [];

  if (fareWithCancellation) {
    const hasIt = brandedFare.cancellation.included || brandedFare.cancellation.charge;
    const refFare = hasIt ? brandedFare : fareWithCancellation;
    const label = refFare.cancellation.charge
      ? t('vacation.select.brandedfares.services.cancellation.charged')
      : refFare.cancellation.details === 'original-payment-method'
        ? t('vacation.select.brandedfares.services.cancellation.original_payment_method')
        : refFare.cancellation.details === 'virtual-credit'
          ? t('vacation.select.brandedfares.services.cancellation.virtual_credit')
          : t('vacation.select.brandedfares.services.cancellation.included');
    cancellationItems.push({
      key: 'cancellation',
      hasIt,
      icon: brandedFare.cancellation.charge ? 'coins' : 'check',
      label,
    });
  }

  if (allFares.some((f) => f.flightChange.included || f.flightChange.charge)) {
    const hasIt = brandedFare.flightChange.included || brandedFare.flightChange.charge;
    const refFare = hasIt ? brandedFare : allFares.find((f) => f.flightChange.included || f.flightChange.charge)!;
    const label = refFare.flightChange.included
      ? t('vacation.select.brandedfares.services.flightChange.included')
      : t('vacation.select.brandedfares.services.flightChange.charged');
    cancellationItems.push({
      key: 'flightChange',
      hasIt,
      icon: brandedFare.flightChange.charge ? 'coins' : 'check',
      label,
    });
  }

  // Group 4: Extras (lounge, comfort, sustainability)
  const extraItems: ServiceItem[] = [];

  if (allFares.some((f) => f.loungeAccess.included || f.loungeAccess.charge)) {
    const hasIt = brandedFare.loungeAccess.included || brandedFare.loungeAccess.charge;
    const refFare = hasIt ? brandedFare : allFares.find((f) => f.loungeAccess.included || f.loungeAccess.charge)!;
    const label = refFare.loungeAccess.included
      ? t('vacation.select.brandedfares.services.loungeAccess.included')
      : t('vacation.select.brandedfares.services.loungeAccess.charged');
    extraItems.push({ key: 'loungeAccess', hasIt, icon: brandedFare.loungeAccess.charge ? 'coins' : 'check', label });
  }

  if (allFares.some((f) => f.comfort.included || f.comfort.charge)) {
    const hasIt = brandedFare.comfort.included || brandedFare.comfort.charge;
    const refFare = hasIt ? brandedFare : allFares.find((f) => f.comfort.included || f.comfort.charge)!;
    const label = refFare.comfort.included
      ? t('vacation.select.brandedfares.services.comfort.included')
      : t('vacation.select.brandedfares.services.comfort.charged');
    extraItems.push({ key: 'comfort', hasIt, icon: brandedFare.comfort.charge ? 'coins' : 'check', label });
  }

  if (allFares.some((f) => f.sustainability?.included)) {
    const refFare = brandedFare.sustainability?.included
      ? brandedFare
      : allFares.find((f) => f.sustainability?.included)!;
    extraItems.push({
      key: 'sustainability',
      hasIt: !!brandedFare.sustainability?.included,
      icon: 'check',
      label: t('vacation.select.brandedfares.services.sustainability.included', {
        percent1: refFare.sustainability!.fuel,
        percent2: refFare.sustainability!.climateProjects,
      }),
    });
  }

  const allGroups = [baggageItems, seatItems, cancellationItems, extraItems];
  const groups = (isDesktop ? allGroups : allGroups.map((g) => g.filter(({ hasIt }) => hasIt))).filter(
    (g) => g.length > 0
  );

  return (
    <ServiceContainer {...rest}>
      {groups.map((group, groupIndex) => (
        <React.Fragment key={group[0].key}>
          {/* MPH-479: inner group dividers only on desktop; mobile relies on the row gap. */}
          {isDesktop && groupIndex > 0 && <EvoDivider />}
          {group.map(({ key, hasIt, icon, label }) => (
            <EvoService key={key} $strikethrough={!hasIt}>
              {hasIt ? icon === 'coins' ? <StyledIconCoins /> : <EvoIconCheck /> : <EvoIconCross />}
              <Text variant="Body" forceTag="span">
                {label}
              </Text>
            </EvoService>
          ))}
        </React.Fragment>
      ))}
    </ServiceContainer>
  );
}
